/**
 * OIDC callback landing.
 *
 * The BFF's ``/api/auth/callback`` route handles the actual code
 * exchange + session-cookie set; it then 302-redirects to ``/``
 * (or the user's saved ``landing_path``). This component is the
 * brief intermediate target the browser hits if Keycloak ever
 * 302s us back to ``/login/callback`` directly rather than via
 * the BFF — in that case we forward to ``/api/auth/login`` so
 * the BFF can drive the flow.
 *
 * In normal operation users never actually render this page; it
 * exists as a safety net + an addressable route for tests.
 */
import { useEffect } from "react";
import { redirectToLogin } from "@/lib/api-client";

export function CallbackPage(): React.ReactElement {
  useEffect(() => {
    // Defer slightly so test environments without ``window``
    // don't navigate in their setup phase.
    const id = window.setTimeout(() => {
      redirectToLogin();
    }, 0);
    return () => window.clearTimeout(id);
  }, []);

  return (
    // ``h-screen`` — a top-level route (no AppShell, no classification frame),
    // so it owns the viewport height directly (was ``h-full`` when the app-root
    // ClassificationFrame supplied the height; the frame was removed 2026-06-12).
    <div className="flex h-screen items-center justify-center bg-background text-muted-foreground">
      <span className="text-sm">Signing in…</span>
    </div>
  );
}
